import React, { useCallback, useEffect, useRef, useState } from "react";
import { message, Modal } from "antd";
import { api } from "@redux/api";
import DeleteWorkordersModal from "./DeleteWorkordersModal";

const CHECKING_KEY = "equipment-delete-impact";

/**
 * 把 settleModal 收到的值统一成 `{ workorderIds: number[] }` 或 null。
 *
 * 兜底：无论上游传 `[1,2]` 还是 `{ workorderIds: [1,2] }`，调用方拿到的形状都一致，
 * 不会因为少一层对象就静默丢掉级联。
 */
const normalizeResolved = (value) => {
  if (!value) return null;
  const ids = Array.isArray(value) ? value : value.workorderIds;
  const workorderIds = (Array.isArray(ids) ? ids : [])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id));
  return { workorderIds };
};

/**
 * 设备删除前的统一确认流程（列表批量删除 / 详情页删除共用）。
 *
 *   const flow = useEquipmentDeleteFlow();
 *   const resolved = await flow.request(equipmentIds);
 *   if (!resolved) return;                       // 用户取消
 *   await api.bulkDeleteEquipment(rows, resolved.workorderIds);
 *   ...
 *   {flow.modalNode}                             // 挂到页面根部
 *
 * 返回值：
 *   null                           用户取消，调用方必须中止删除
 *   { workorderIds: number[] }     确认继续；workorderIds 为空表示只删设备
 *
 * 有「删完会失去全部设备」的工单时，勾选弹窗本身就是确认步骤，不再叠一层确认框；
 * 否则退回普通二次确认。
 */
export default function useEquipmentDeleteFlow() {
  const [pending, setPending] = useState(null);
  const resolverRef = useRef(null);
  // 防连点：一次只有一个删除确认在进行中
  const busyRef = useRef(false);

  // 组件卸载时兜底 resolve，避免调用方 await 永久挂起
  useEffect(
    () => () => {
      const resolve = resolverRef.current;
      resolverRef.current = null;
      if (resolve) resolve(null);
    },
    []
  );

  const settleModal = useCallback((value) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    busyRef.current = false;
    setPending(null);
    if (resolve) resolve(normalizeResolved(value));
  }, []);

  const request = useCallback(async (equipmentIds) => {
    const ids = (equipmentIds || [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
    if (!ids.length || busyRef.current) return null;
    busyRef.current = true;

    let impact;
    message.loading({ content: "Checking related work orders...", key: CHECKING_KEY, duration: 0 });
    try {
      const response = await api.getEquipmentDeleteImpact(ids);
      impact = response && response.data;
    } catch (error) {
      impact = null;
    } finally {
      message.destroy(CHECKING_KEY);
    }

    if (!impact) {
      busyRef.current = false;
      message.error("Failed to check related work orders.");
      return null;
    }

    const workorders = impact.workorders || [];
    const canDeleteWorkorders = impact.can_delete_workorders !== false;

    // 没有会失去全部设备的工单，或当前用户无权删除工单：只做普通二次确认
    if (!workorders.length || !canDeleteWorkorders) {
      return new Promise((resolve) => {
        const settle = (value) => {
          busyRef.current = false;
          resolve(value);
        };
        Modal.confirm({
          title:
            ids.length > 1
              ? `Delete these ${ids.length} equipment?`
              : "Delete this equipment?",
          content: "This action cannot be undone.",
          okText: "Delete",
          okType: "danger",
          cancelText: "Cancel",
          onOk: () => settle({ workorderIds: [] }),
          onCancel: () => settle(null),
        });
      });
    }

    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setPending({ workorders, canDeleteWorkorders });
    });
  }, []);

  const modalNode = (
    <DeleteWorkordersModal
      open={!!pending}
      workorders={pending?.workorders || []}
      canDeleteWorkorders={pending?.canDeleteWorkorders !== false}
      onCancel={() => settleModal(null)}
      // ⚠️ 必须包成 { workorderIds }：调用方读的是 resolved.workorderIds，
      // 早先这里直接 settleModal(ids) 会把裸数组 resolve 出去，
      // 导致 resolved.workorderIds === undefined → 请求不带 workorder_ids
      // → 只删设备、工单一条都不删（表现为 deleted_workorder_ids 为空）。
      onOk={(ids) => settleModal({ workorderIds: ids || [] })}
    />
  );

  return { request, modalNode };
}
